import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api, isProblemError, setUnauthenticatedHandler } from "../api";
import type { Company, Me } from "../api";
import { useToast } from "../components";
import { ROUTES } from "../shell/routes";
import { SessionContext } from "./context";
import type { SessionApi } from "./context";
import { SESSION_INITIAL, sessionReducer } from "./reducer";

/**
 * Tient l'état de session de l'application.
 *
 * Au montage, un seul appel à `/me` décide : authentifié, anonyme (401), ou
 * indisponible (panne). Ensuite, deux sources font bouger l'état : les gestes
 * explicites (connexion, déconnexion, relecture) et le client d'API, qui
 * signale un 401 reçu sur une route protégée — la session a expiré ou a été
 * révoquée par un administrateur.
 *
 * Rien n'est écrit dans `localStorage` : l'état vit en mémoire, la vérité est
 * le cookie, et lui, le script ne le voit pas.
 *
 * Placé sous `BrowserRouter` (il navigue à la déconnexion) et sous
 * `ToastProvider` (il prévient quand la session tombe).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sessionReducer, SESSION_INITIAL);
  const navigate = useNavigate();
  const { notify } = useToast();

  const refresh = useCallback(async () => {
    try {
      const me = await api.getMe();
      dispatch({ type: "resolved", me });
    } catch (cause: unknown) {
      if (isProblemError(cause) && cause.status === 401) {
        dispatch({ type: "unauthenticated" });
        return;
      }
      dispatch({ type: "unavailable", error: cause });
    }
  }, []);

  // Première résolution. StrictMode joue l'effet deux fois en développement :
  // la seconde réponse écrase la première avec la même valeur, sans effet.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Session perdue en cours de route : le client d'API le dit, on repasse en
  // anonyme et on le dit à l'utilisateur. La redirection est déclarative — la
  // garde de route voit l'état anonyme et renvoie vers la connexion en
  // mémorisant l'écran quitté.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      dispatch({ type: "unauthenticated" });
      notify({
        title: "Votre session a expiré",
        description: "Reconnectez-vous pour continuer.",
        tone: "warning",
      });
    });
    return () => setUnauthenticatedHandler(null);
  }, [notify]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await api.login({ email, password });
    dispatch({ type: "resolved", me });
    return me;
  }, []);

  const adopt = useCallback((me: Me) => {
    dispatch({ type: "resolved", me });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Le serveur n'a pas répondu : le cookie survit peut-être, mais côté
      // application on part quand même. La prochaine visite refera `/me`.
    }
    dispatch({ type: "unauthenticated" });
    // Explicite, et non par la garde : une déconnexion volontaire ne doit pas
    // mémoriser l'écran quitté pour y revenir après reconnexion.
    navigate(ROUTES.connexion, { replace: true });
  }, [navigate]);

  const setCompany = useCallback((company: Company) => {
    dispatch({ type: "companyUpdated", company });
  }, []);

  const value = useMemo<SessionApi>(
    () => ({ state, login, adopt, logout, refresh, setCompany }),
    [state, login, adopt, logout, refresh, setCompany],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
